import React, { useState, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  useNotifications,
  createNotification,
  NOTIFICATION_TYPES,
} from "../../context/NotificationsContext";
import {
  respondToComplaint,
  transferComplaint,
} from "../../api/complaints";
import {
  COMPLAINT_KIND,
  COMPLAINT_STATUS,
  getComplaintsForRole,
  getRoleLabel,
  isComplaintNeedingViewerAction,
} from "../../utils/complaintHelpers";
import {
  isHrFamilyRole,
  isOwnerRole,
} from "../../utils/hrStaffHelpers";
import ComplaintCard from "../common/ComplaintCard";
import { queueInvestigationDraft } from "../investigations/investigationDraft";
import { useNavigation } from "../../context/NavigationContext";
import ComplaintResponseModal from "../common/ComplaintResponseModal";
import ComplaintTransferModal from "../common/ComplaintTransferModal";
import "./ReceivedComplaints.css";

export default function ReceivedComplaints({ userRole }) {
  const { user } = useAuth();
  const navigation = useNavigation();
  const { complaints, refreshData, findRoleEmployee } = useData();
  const { addNotificationToUser } = useNotifications();

  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [responseModal, setResponseModal] = useState({
    open: false,
    complaint: null,
  });
  const [transferModal, setTransferModal] = useState({
    open: false,
    complaint: null,
  });

  const isHrOverview =
    isHrFamilyRole(userRole) || userRole === "gm" || isOwnerRole(userRole);

  const visibleComplaints = useMemo(
    () =>
      getComplaintsForRole(
        complaints,
        userRole,
        user?.id,
        user?.backendRoles || [],
      ),
    [complaints, userRole, user?.id, user?.backendRoles],
  );

  const filteredComplaints = useMemo(() => {
    return visibleComplaints
      .filter((c) => {
        if (filter === "all") return true;
        if (filter === "pending") return c.status === COMPLAINT_STATUS.PENDING;
        if (filter === "in_progress")
          return c.status === COMPLAINT_STATUS.IN_PROGRESS;
        if (filter === "resolved")
          return (
            c.status === COMPLAINT_STATUS.RESOLVED ||
            c.status === COMPLAINT_STATUS.REJECTED
          );
        return true;
      })
      .filter((c) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          c.title?.toLowerCase().includes(term) ||
          c.senderName?.toLowerCase().includes(term) ||
          c.receiverName?.toLowerCase().includes(term) ||
          getRoleLabel(c.receiverRole)?.includes(term) ||
          c.description?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [visibleComplaints, filter, searchTerm]);

  const pendingCount = visibleComplaints.filter(
    (c) =>
      c.status === COMPLAINT_STATUS.PENDING ||
      c.status === COMPLAINT_STATUS.IN_PROGRESS,
  ).length;

  function openResponseModal(complaint) {
    setResponseModal({ open: true, complaint });
  }

  function closeResponseModal() {
    setResponseModal({ open: false, complaint: null });
  }

  async function handleSubmitResponse({ status, response }) {
    const complaint = responseModal.complaint;
    if (!complaint) return;

    const outcome =
      status === COMPLAINT_STATUS.RESOLVED
        ? "RESOLVED"
        : status === COMPLAINT_STATUS.REJECTED
          ? "REJECTED"
          : "IN_PROGRESS";

    try {
      await respondToComplaint(complaint.id, {
        outcome,
        notes: response,
      });

      await refreshData();

      const statusLabel =
        status === COMPLAINT_STATUS.RESOLVED
          ? "تم حل"
          : status === COMPLAINT_STATUS.REJECTED
            ? "مرفوض"
            : "قيد المعالجة";

      const notif = createNotification({
        recipientId: complaint.senderId,
        recipientRole: complaint.senderRole,
        type: NOTIFICATION_TYPES.COMPLAINT_RESPONSE,
        title: "💬 رد على شكواك",
        message: `تم ${statusLabel} شكواك "${complaint.title}". الرد: ${response}`,
        relatedRequestId: complaint.id,
        senderName: user.name,
        senderRole: userRole,
        requestType: "complaint",
        requestStatus: status,
      });
      addNotificationToUser(complaint.senderId, notif);

      const isFinalStatus =
        status === COMPLAINT_STATUS.RESOLVED ||
        status === COMPLAINT_STATUS.REJECTED;

      const hrEmployee = findRoleEmployee("hr");

      if (isFinalStatus && userRole !== "hr" && hrEmployee?.id) {
        const hrTitle =
          status === COMPLAINT_STATUS.RESOLVED
            ? "✅ شكوى تم حلها"
            : "❌ شكوى مرفوضة";

        const hrNotif = createNotification({
          recipientId: hrEmployee.id,
          recipientRole: "hr",
          type: NOTIFICATION_TYPES.COMPLAINT_RESPONSE,
          title: hrTitle,
          message: `تم ${statusLabel} شكوى "${complaint.title}" من ${complaint.senderName} (موجهة إلى ${complaint.receiverName}) بواسطة ${user.name}. الرد: ${response}`,
          relatedRequestId: complaint.id,
          senderName: user.name,
          senderRole: userRole,
          requestType: "complaint",
          requestStatus: status,
        });
        addNotificationToUser(hrEmployee.id, hrNotif);
      }

      closeResponseModal();
    } catch (err) {
      console.error("Failed to respond to complaint", err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر إرسال الرد أو تحديث حالة الشكوى";
      alert(Array.isArray(message) ? message.join("\n") : message);
    }
  }

  function openTransferModal(complaint) {
    setTransferModal({ open: true, complaint });
  }

  function closeTransferModal() {
    setTransferModal({ open: false, complaint: null });
  }

  async function handleSubmitTransfer({ targetRole, targetId, notes }) {
    const complaint = transferModal.complaint;
    if (!complaint) return;

    try {
      await transferComplaint(complaint.id, { targetRole, targetId, notes });
      await refreshData();

      const notif = createNotification({
        recipientId: complaint.senderId,
        recipientRole: complaint.senderRole,
        type: NOTIFICATION_TYPES.COMPLAINT_RESPONSE,
        title: "تم تحويل شكواك",
        message: `تم تحويل شكواك "${complaint.title}". السبب: ${notes}`,
        relatedRequestId: complaint.id,
        senderName: user.name,
        senderRole: userRole,
        requestType: "complaint",
        requestStatus: COMPLAINT_STATUS.IN_PROGRESS,
      });
      addNotificationToUser(complaint.senderId, notif);

      closeTransferModal();
    } catch (err) {
      console.error("Failed to transfer complaint", err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "تعذر تحويل الشكوى";
      alert(Array.isArray(message) ? message.join("\n") : message);
    }
  }

  function canAct(complaint) {
    return isComplaintNeedingViewerAction(
      complaint,
      userRole,
      user?.backendRoles || [],
      user?.id,
    );
  }

  return (
    <div className="received-complaints" dir="rtl">
      <div className="ess-home-header">
        <h1 className="ess-home-title">
          {isHrOverview ? "متابعة الشكاوى والاستفسارات" : "الشكاوى والاستفسارات الواردة"}
        </h1>
        <p className="ess-home-subtitle">
          {userRole === "gm"
            ? `${visibleComplaints.filter((c) => canAct(c)).length} تحتاج إجراءك | ${visibleComplaints.length} في النظام`
            : isHrOverview
              ? `${visibleComplaints.length} في النظام | ${pendingCount} نشطة`
              : `${visibleComplaints.length} واردة | ${pendingCount} تحتاج متابعة`}
        </p>
      </div>

      <div className="rc-filters">
        <button
          className={`rc-filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل
        </button>
        <button
          className={`rc-filter-btn ${filter === "pending" ? "active" : ""}`}
          onClick={() => setFilter("pending")}
        >
          قيد الانتظار
        </button>
        <button
          className={`rc-filter-btn ${filter === "in_progress" ? "active" : ""}`}
          onClick={() => setFilter("in_progress")}
        >
          قيد المعالجة
        </button>
        <button
          className={`rc-filter-btn ${filter === "resolved" ? "active" : ""}`}
          onClick={() => setFilter("resolved")}
        >
          محسومة
        </button>
      </div>

      {(userRole === "manager" ||
        userRole === "department-manager" ||
        isHrFamilyRole(userRole) ||
        userRole === "gm" ||
        isOwnerRole(userRole)) && (
        <div className="rc-search">
          <input
            type="text"
            className="rc-search-input"
            placeholder={
              isHrOverview
                ? "بحث بالعنوان أو المرسل أو المستلم..."
                : "بحث بالعنوان أو المرسل..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="rc-list">
        {filteredComplaints.length === 0 ? (
          <div className="rc-empty">
            <p>
              {isHrOverview ? "📋 لا توجد شكاوى في النظام" : "📋 لا توجد شكاوى واردة"}
            </p>
          </div>
        ) : (
          filteredComplaints.map((c) => (
            <ComplaintCard
              key={c.id}
              complaint={c}
              viewMode={isHrOverview ? "overview" : "received"}
              canRespond={canAct(c)}
              onRespond={openResponseModal}
              canTransfer={canAct(c)}
              onTransfer={openTransferModal}
              canOpenInvestigation={
                isHrFamilyRole(userRole) &&
                (c.kind || COMPLAINT_KIND.COMPLAINT) === COMPLAINT_KIND.COMPLAINT
              }
              onOpenInvestigation={(complaint) => {
                queueInvestigationDraft(complaint);
                navigation?.navigate("investigations");
              }}
            />
          ))
        )}
      </div>

      <ComplaintResponseModal
        isOpen={responseModal.open}
        onClose={closeResponseModal}
        complaint={responseModal.complaint}
        onSubmit={handleSubmitResponse}
      />

      <ComplaintTransferModal
        isOpen={transferModal.open}
        onClose={closeTransferModal}
        complaint={transferModal.complaint}
        onSubmit={handleSubmitTransfer}
      />
    </div>
  );
}
